import React, { useEffect, useMemo, useState } from 'react';
import { Instagram, Facebook, AlertTriangle, Loader2, Link2Off } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import apiClient from '../utils/api';

// Map a campaign/template type to the most natural placement for each platform.
//   post     -> Feed on IG + FB
//   story    -> Story on IG + FB
//   reel     -> Reel on IG, Feed on FB (FB Reels for Pages is gated to some Pages — keep Feed safe default)
//   default  -> Feed
const defaultPlacementFor = (campaignType, platform) => {
  const t = (campaignType || 'post').toLowerCase();
  if (t === 'story') return 'story';
  if (t === 'reel') return platform === 'instagram' ? 'reel' : 'feed';
  return 'feed';
};

const PLACEMENT_LABEL = {
  feed: 'Feed',
  story: 'Story',
  reel: 'Reels',
};

const PLACEMENTS_FOR_PLATFORM = {
  instagram: ['feed', 'story', 'reel'],
  facebook: ['feed', 'story'],
};

// Naive aspect-ratio compatibility check. Returns null if compatible, else a
// human-readable warning string. We don't *block* the publish — Meta itself
// will auto-crop — but we surface the mismatch so the user isn't surprised.
const aspectMismatchWarning = (campaignType, placement) => {
  const t = (campaignType || 'post').toLowerCase();
  if (t === 'post' && (placement === 'story' || placement === 'reel')) {
    return 'Your 1:1 image will be cropped or letterboxed for this vertical placement.';
  }
  if ((t === 'story' || t === 'reel') && placement === 'feed') {
    return 'Your 9:16 media will be cropped or letterboxed in the Feed.';
  }
  return null;
};

/**
 * <PublishDestinationPicker /> — controlled component.
 *
 * Props:
 *   campaignType: 'post' | 'story' | 'reel' | 'carousel' (drives defaults)
 *   value: array of { account_id, placement } currently selected
 *   onChange: (newSelections) => void
 *   compact: boolean — render in compact mode (no header card)
 *
 * Fetches /api/social/accounts on mount. If the user has zero connected
 * accounts, shows a CTA to /profile?tab=social. Otherwise shows one row per
 * connected account with a placement chip-picker (Feed / Story / Reels)
 * filtered to what that platform supports.
 */
export const PublishDestinationPicker = ({ campaignType = 'post', value = [], onChange, compact = false }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiClient.get('/social/accounts');
        const accs = resp.data?.accounts || [];
        if (cancelled) return;
        setAccounts(accs);
        // Seed defaults on first load — all accounts selected, with the
        // best-fit placement per platform/campaign type. Parent can override
        // by passing a value prop after first mount.
        if (value.length === 0 && accs.length > 0 && onChange) {
          onChange(
            accs.map(a => ({
              account_id: a.account_id,
              placement: defaultPlacementFor(campaignType, a.platform),
            }))
          );
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load connected accounts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // We intentionally only run this on mount — re-seeding on every parent
    // re-render would clobber the user's selections.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedMap = useMemo(() => {
    const m = new Map();
    for (const v of value) m.set(v.account_id, v.placement);
    return m;
  }, [value]);

  const setSelection = (account_id, placement, checked) => {
    const next = [...value];
    const idx = next.findIndex(v => v.account_id === account_id);
    if (!checked) {
      if (idx >= 0) next.splice(idx, 1);
    } else if (idx >= 0) {
      next[idx] = { account_id, placement };
    } else {
      next.push({ account_id, placement });
    }
    onChange && onChange(next);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2" data-testid="publish-picker-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading connected accounts…
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" data-testid="publish-picker-error">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (accounts.length === 0) {
    return (
      <Alert data-testid="publish-picker-no-accounts">
        <Link2Off className="h-4 w-4" />
        <AlertDescription>
          No connected social accounts yet.&nbsp;
          <a className="underline font-medium" href="/profile?tab=social" data-testid="connect-accounts-link">
            Connect Instagram or Facebook
          </a> to publish from RealProp.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'} data-testid="publish-destination-picker">
      {!compact && (
        <div>
          <p className="text-sm font-medium">Publish to</p>
          <p className="text-xs text-muted-foreground">Pick which connected accounts and placements to post to.</p>
        </div>
      )}

      {accounts.map(acc => {
        const isInstagram = acc.platform === 'instagram';
        const Icon = isInstagram ? Instagram : Facebook;
        const placements = PLACEMENTS_FOR_PLATFORM[acc.platform] || ['feed'];
        const selected = selectedMap.get(acc.account_id);
        const isChecked = !!selected;
        const placement = selected || defaultPlacementFor(campaignType, acc.platform);
        const warn = isChecked ? aspectMismatchWarning(campaignType, placement) : null;
        const accountTestId = `publish-dest-${acc.account_id}`;

        return (
          <div key={acc.account_id} className="rounded-md border p-3 space-y-2" data-testid={accountTestId}>
            <div className="flex items-center gap-3">
              <Checkbox
                checked={isChecked}
                onCheckedChange={(c) => setSelection(acc.account_id, placement, c)}
                data-testid={`${accountTestId}-checkbox`}
              />
              <Icon className={`h-4 w-4 ${isInstagram ? 'text-pink-500' : 'text-blue-600'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {acc.account_name || acc.username || acc.account_id}
                  <Badge variant="outline" className="ml-2 text-[10px] capitalize">{acc.platform}</Badge>
                </p>
              </div>
            </div>

            {isChecked && (
              <div className="pl-7 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">Placement:</span>
                {placements.map(p => (
                  <button
                    type="button"
                    key={p}
                    onClick={() => setSelection(acc.account_id, p, true)}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                      placement === p
                        ? 'bg-foreground text-background border-foreground'
                        : 'bg-background hover:bg-muted'
                    }`}
                    data-testid={`${accountTestId}-placement-${p}`}
                  >
                    {PLACEMENT_LABEL[p]}
                  </button>
                ))}
              </div>
            )}

            {warn && (
              <div className="pl-7 flex items-start gap-1.5 text-xs text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{warn}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PublishDestinationPicker;
